import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import HomePage from './pages/HomePage';
import FilmPage from './pages/FilmPage';
import AdminPage from './pages/AdminPage';
import ChatPage from './pages/ChatPage';
import BattlePage from './pages/BattlePage';
import RankingsPage from './pages/RankingsPage';
import TasteProfilePage from './pages/TasteProfilePage';
import AuthModal from './components/AuthModal';

// ── Global styles ──────────────────────────────────────────────
const style = document.createElement('style');
style.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --gold:       #D4AF37;
    --gold-light: #F0D060;
    --gold-dim:   #8B7028;
    --black:      #040406;
    --surface:    #0A0A0F;
    --surface2:   #111118;
    --surface3:   #18181F;
    --border:     #1E1E2A;
    --border2:    #2A2A38;
    --text:       #E8E0CC;
    --text-dim:   #8A8070;
    --text-muted: #4A4440;
    --red:        #C0392B;
    --green:      #27AE60;
  }

  html { scroll-behavior: smooth; }

  body {
    background: var(--black);
    color: var(--text);
    font-family: 'Crimson Text', Georgia, serif;
    font-size: 17px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }

  ::selection { background: var(--gold); color: var(--black); }

  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: var(--surface); }
  ::-webkit-scrollbar-thumb { background: var(--gold-dim); border-radius: 3px; }

  input, textarea, select { font-family: 'Crimson Text', Georgia, serif; font-size: 16px; }
  button { font-family: 'Crimson Text', Georgia, serif; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .fade-up   { animation: fadeUp 0.4s ease both; }
  .fade-up-1 { animation: fadeUp 0.4s 0.05s ease both; }
  .fade-up-2 { animation: fadeUp 0.4s 0.10s ease both; }
  .fade-up-3 { animation: fadeUp 0.4s 0.15s ease both; }
  .fade-up-4 { animation: fadeUp 0.4s 0.20s ease both; }

  /* Global responsive */
  * { -webkit-tap-highlight-color: transparent; }
  img { max-width: 100%; }
  button { touch-action: manipulation; }
  @media (max-width: 640px) {
    body { font-size: 15px; }
  }
`;
document.head.appendChild(style);

// ── page state: 'home' | 'film' | 'admin'
function Router() {
  const [page, setPage]       = useState(() => window.location.pathname.startsWith('/admin') ? 'admin' : window.location.pathname.startsWith('/chat') ? 'chat' : window.location.pathname.startsWith('/battle') ? 'battle' : window.location.pathname.startsWith('/rankings') ? 'rankings' : window.location.pathname.startsWith('/taste') ? 'taste' : 'home');
  const [selectedFilm, setSelectedFilm] = useState(null);
  const [showAuth, setShowAuth]         = useState(false);

  // Browser back/forward
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      if (path.startsWith('/admin')) { setPage('admin'); setSelectedFilm(null); }
      else if (path.startsWith('/film/')) { setPage('film'); }
      else if (path.startsWith('/chat'))     { setPage('chat');     setSelectedFilm(null); }
      else if (path.startsWith('/battle'))   { setPage('battle');   setSelectedFilm(null); }
      else if (path.startsWith('/rankings')) { setPage('rankings'); setSelectedFilm(null); }
      else if (path.startsWith('/taste'))    { setPage('taste');    setSelectedFilm(null); }
      else { setPage('home'); setSelectedFilm(null); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (p, film = null) => {
    if (p === 'chat')     { window.history.pushState({}, '', '/chat');     setPage('chat');     setSelectedFilm(null); }
    else if (p === 'battle')   { window.history.pushState({}, '', '/battle');   setPage('battle');   setSelectedFilm(null); }
    else if (p === 'rankings') { window.history.pushState({}, '', '/rankings'); setPage('rankings'); setSelectedFilm(null); }
    else if (p === 'taste')    { window.history.pushState({}, '', '/taste');    setPage('taste');    setSelectedFilm(null); }
    else if (p === 'admin') { window.history.pushState({}, '', '/admin'); setPage('admin'); setSelectedFilm(null); }
    else if (p === 'film' && film) {
      const slug = film.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      window.history.pushState({}, '', `/film/${slug}`);
      setSelectedFilm(film);
      setPage('film');
    } else {
      window.history.pushState({}, '', '/');
      setPage('home');
      setSelectedFilm(null);
    }
  };

  if (page === 'battle') {
    return (
      <>
        <BattlePage onAuthOpen={() => setShowAuth(true)} onAdminClick={() => navigate('admin')} onChatClick={() => navigate('chat')} onBattleClick={() => navigate('battle')} onRankingsClick={() => navigate('rankings')} onTasteClick={() => navigate('taste')} />
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      </>
    );
  }

  if (page === 'rankings') {
    return (
      <>
        <RankingsPage onAuthOpen={() => setShowAuth(true)} onAdminClick={() => navigate('admin')} onChatClick={() => navigate('chat')} onBattleClick={() => navigate('battle')} onRankingsClick={() => navigate('rankings')} onTasteClick={() => navigate('taste')} onFilmClick={(film) => navigate('film', film)} />
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      </>
    );
  }

  if (page === 'taste') {
    return (
      <>
        <TasteProfilePage onAuthOpen={() => setShowAuth(true)} onAdminClick={() => navigate('admin')} onChatClick={() => navigate('chat')} onBattleClick={() => navigate('battle')} onRankingsClick={() => navigate('rankings')} onTasteClick={() => navigate('taste')} onFilmClick={(film) => navigate('film', film)} />
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      </>
    );
  }

  if (page === 'chat') {
    return (
      <>
        <ChatPage
          onAuthOpen={() => setShowAuth(true)}
          onAdminClick={() => navigate('admin')}
          onFilmClick={(film) => navigate('film', film)}
          onBattleClick={() => navigate('battle')}
          onRankingsClick={() => navigate('rankings')}
          onTasteClick={() => navigate('taste')}
        />
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      </>
    );
  }

  if (page === 'admin') {
    return (
      <>
        <AdminPage onBack={() => navigate('home')} onNeedAuth={() => setShowAuth(true)} onChatClick={() => navigate('chat')}
          onBattleClick={() => navigate('battle')}
          onRankingsClick={() => navigate('rankings')}
          onTasteClick={() => navigate('taste')} />
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      </>
    );
  }

  if (page === 'film' && selectedFilm) {
    return (
      <>
        <FilmPage
          film={selectedFilm}
          onBack={() => navigate('home')}
          onAuthOpen={() => setShowAuth(true)}
          onAdminClick={() => navigate('admin')}
          onChatClick={() => navigate('chat')}
          onBattleClick={() => navigate('battle')}
          onRankingsClick={() => navigate('rankings')}
          onTasteClick={() => navigate('taste')}
        />
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      </>
    );
  }

  return (
    <>
      <HomePage
        onFilmClick={(film) => navigate('film', film)}
        onAdminClick={() => navigate('admin')}
        onChatClick={() => navigate('chat')}
          onBattleClick={() => navigate('battle')}
          onRankingsClick={() => navigate('rankings')}
          onTasteClick={() => navigate('taste')}
        onAuthOpen={() => setShowAuth(true)}
      />
      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router />
    </AuthProvider>
  );
}
